import type { CandidateDocument, ChecklistItem, DocumentType } from "@shared/modules/x-documents";
import { api, ApiError } from "../../../core/lib/api";

const BASE = import.meta.env.VITE_API_BASE_URL || "";

export function listTypes(): Promise<DocumentType[]> {
  return api<DocumentType[]>("/api/documents/types");
}

export function candidateDocuments(candidateId: string): Promise<{ documents: CandidateDocument[]; checklist: ChecklistItem[] }> {
  return api(`/api/documents/candidat/${candidateId}`);
}

export interface UploadResult {
  created: CandidateDocument[];
  rejected: { name: string; reason: string }[];
  checklist?: ChecklistItem[];
}

/** Dépôt multipart : `api()` force le JSON, on passe donc par fetch directement. */
async function postFiles(path: string, files: File[], fields: Record<string, string> = {}): Promise<UploadResult> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v) form.set(k, v);
  for (const f of files) form.append("fichiers", f, f.name);
  const res = await fetch(`${BASE}${path}`, { method: "POST", body: form, credentials: "include" });
  const body = (await res.json().catch(() => ({}))) as UploadResult & { error?: string };
  if (!res.ok && !body.created?.length) throw new ApiError(res.status, body.error ?? "upload_failed", body);
  return body;
}

export function uploadToCandidate(candidateId: string, files: File[], typeCode: string, expiresAt?: string): Promise<UploadResult> {
  return postFiles(`/api/documents/candidat/${candidateId}`, files, { typeCode, expiresAt: expiresAt ?? "" });
}

export function importCvs(files: File[]): Promise<UploadResult> {
  return postFiles("/api/documents/importations", files);
}

export function downloadUrl(documentId: string, inline = false): string {
  return `${BASE}/api/documents/${documentId}/telecharger${inline ? "?inline=1" : ""}`;
}

export function updateDocument(id: string, patch: { status?: string; typeCode?: string; expiresAt?: string | null }): Promise<CandidateDocument> {
  return api<CandidateDocument>(`/api/documents/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export function deleteDocument(id: string): Promise<void> {
  return api<void>(`/api/documents/${id}`, { method: "DELETE" });
}

export function setChecklist(candidateId: string, requiredTypes: string[]): Promise<ChecklistItem[]> {
  return api<ChecklistItem[]>(`/api/documents/candidat/${candidateId}/checklist`, { method: "PUT", body: JSON.stringify({ requiredTypes }) });
}

export function expirations(days = 90): Promise<(CandidateDocument & { reference: string | null })[]> {
  return api(`/api/documents/expirations?days=${days}`);
}

/** Lecture d'une pièce par le module x-extraction (appel d'API, aucun import de code). */
export function requestReading(documentId: string, kind: "cv" | "piece"): Promise<{ id: string }> {
  return api<{ id: string }>(`/api/extraction/documents/${documentId}`, { method: "POST", body: JSON.stringify({ kind }) });
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
